import QRCode from 'qrcode';
import type { AccessId } from '@/types/org';
import { orgService } from '@/services/org-service';
import { navigate } from '@/utils/navigate';
import './nu-user-menu';
import { getAppConfig } from '@/services/app-config';

class NuQrPrint extends HTMLElement {
  private readonly root: ShadowRoot;
  private _initialized = false;
  private _loading = false;
  private _loadError = '';
  private _orgName = '';
  private _items: { a: AccessId; svg: string }[] = [];
  private _size: 'large' | 'small' | 'tiny' = 'large';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['org-id']; }
  get orgId() { return this.getAttribute('org-id') ?? ''; }

  connectedCallback() {
    if (!this._initialized) {
      this._initialized = true;
      this._loading = true;
      this._render();
      this._load();
    }
  }

  private async _load() {
    try {
      const [org, { appBaseUrl }] = await Promise.all([
        orgService.getOrgById(this.orgId),
        getAppConfig(),
      ]);
      this._orgName = org.name;
      const enabled = org.accessIds.filter(a => !a.disabled);
      this._items = await Promise.all(enabled.map(async a => {
        const svg = await QRCode.toString(`${appBaseUrl}/app/scan/${a.token}`, { type: 'svg', margin: 1 });
        return { a, svg };
      }));
      this._loading = false;
    } catch (err) {
      this._loadError = err instanceof Error ? err.message : 'Failed to load.';
      this._loading = false;
    }
    this._render();
  }

  private _render() {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>QR Codes${this._orgName ? ` — ${this._orgName}` : ''}</span>
          <div class="toolbar-actions">
            ${!this._loading && !this._loadError && this._items.length > 0 ? `
              <label class="size-label">Size:
                <select class="size-select" id="qr-size">
                  <option value="large">2" × 2"</option>
                  <option value="small">1" × 1"</option>
                  <option value="tiny">½" × ½"</option>
                </select>
              </label>
              <button class="btn-done" id="print-btn">Print</button>
            ` : ''}
            <button class="btn-back" id="back-btn">← Back</button>
            <nu-user-menu></nu-user-menu>
          </div>
        </header>
        ${this._loading ? `
          <div class="body-msg"><p class="mono">Loading…</p></div>
        ` : this._loadError ? `
          <div class="body-msg"><p class="error">${this._loadError}</p></div>
        ` : this._items.length === 0 ? `
          <div class="body-msg"><p class="mono">No enabled access tokens found.</p></div>
        ` : `
          <div class="qr-grid" id="qr-grid">
            ${this._items.map(({ a, svg }) => `
              <div class="qr-item">
                <div class="qr-code">${svg}</div>
                <div class="qr-brand">notify-u <span class="dot"></span></div>
                ${a.name ? `<div class="qr-name">${a.name}</div>` : ''}
              </div>
            `).join('')}
          </div>
        `}
      </div>
    `;

    this.root.querySelector('#back-btn')!.addEventListener('click', () => {
      navigate(`/app/org/${this.orgId}`);
    });
    this.root.querySelector('#qr-size')?.addEventListener('change', (e) => {
      this._size = (e.target as HTMLSelectElement).value as 'large' | 'small' | 'tiny';
      const grid = this.root.querySelector<HTMLElement>('#qr-grid');
      if (grid) {
        grid.classList.toggle('qr-grid--small', this._size === 'small');
        grid.classList.toggle('qr-grid--tiny', this._size === 'tiny');
      }
    });
    this.root.querySelector('#print-btn')?.addEventListener('click', () => window.print());
  }

  private _styles(): string {
    return `<style>
      :host { display: block; }
      *, *::before, *::after { box-sizing: border-box; }

      .page {
        min-height: 100vh;
        background: var(--paper-light, #FAF7F0);
        color: var(--ink, #2A2622);
        font-family: 'Inter', sans-serif;
      }
      .mono { font-family: 'IBM Plex Mono', monospace; }

      .top-bar {
        display: flex; align-items: center; justify-content: space-between;
        flex-wrap: wrap; gap: 12px;
        padding: 12px 20px;
        border-bottom: 1px solid var(--line, #C9C0AC);
        background: rgba(250,247,240,0.95);
        backdrop-filter: blur(6px);
        position: sticky; top: 0; z-index: 10;
      }
      .brand {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700; font-size: 18px; letter-spacing: -0.02em;
        display: flex; align-items: center; gap: 7px;
      }
      .dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: var(--kiln, #C97A2E);
        display: inline-block; flex-shrink: 0;
      }

      .toolbar-actions { display: flex; align-items: center; gap: 10px; }
      .size-label {
        display: flex; align-items: center; gap: 6px;
        font-size: 13px; color: var(--ink-soft, #5A5346);
      }
      .size-select {
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 4px; padding: 4px 8px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink, #2A2622); cursor: pointer;
      }
      .btn-done {
        background: var(--kiln, #C97A2E);
        color: var(--paper-light, #FAF7F0);
        border: none; border-radius: 4px;
        padding: 6px 14px;
        font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
        cursor: pointer; transition: background .15s;
      }
      .btn-done:hover { background: var(--kiln-dark, #A15F1F); }
      .btn-back {
        background: transparent;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px; padding: 5px 12px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink-soft, #5A5346);
        cursor: pointer; transition: background .15s;
      }
      .btn-back:hover { background: var(--paper-light, #FAF7F0); }

      .body-msg {
        display: flex; justify-content: center; padding: 60px 24px;
        font-size: 14px; color: var(--ink-soft, #5A5346);
      }
      .error { color: #c0392b; }

      .qr-grid {
        display: flex; flex-wrap: wrap; gap: 20px;
        padding: 24px; align-items: flex-start;
      }
      .qr-item {
        display: flex; flex-direction: column; align-items: center; gap: 8px;
        padding: 16px;
        background: white;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        break-inside: avoid; page-break-inside: avoid;
      }
      .qr-code svg { width: 192px; height: 192px; display: block; }
      .qr-grid--small .qr-code svg { width: 96px; height: 96px; }
      .qr-grid--tiny .qr-code svg { width: 48px; height: 48px; }
      .qr-brand {
        font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 700;
        color: var(--ink, #2A2622);
        display: flex; align-items: center; justify-content: center; gap: 5px;
      }
      .qr-name {
        font-family: 'Space Grotesk', sans-serif; font-size: 12px; font-weight: 500;
        color: var(--ink-soft, #5A5346);
        max-width: 192px; min-width: 0;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .qr-grid--small .qr-name { max-width: 96px; }
      .qr-grid--tiny .qr-name { max-width: 48px; font-size: 10px; }

      @media print {
        .top-bar { display: none !important; }
        .qr-grid { padding: 0; gap: 8px; }
        .qr-item { border: 1px solid #ccc; border-radius: 4px; }
        .qr-code svg { width: 2in !important; height: 2in !important; }
        .qr-grid--small .qr-code svg { width: 1in !important; height: 1in !important; }
        .qr-grid--tiny .qr-code svg { width: 0.5in !important; height: 0.5in !important; }
      }
    </style>`;
  }
}

customElements.define('nu-qr-print', NuQrPrint);